/**
 * Registrar insumos: «llegó producto» y «se usó producto».
 *
 * Tocar antes que escribir (la línea que el dueño viene pidiendo para toda la
 * app del personal): los productos son tarjetas, la acción son dos botones
 * grandes, la cantidad es un stepper. Escribir —la nota— es opcional y viene
 * al final. Se usa de pie, con guantes, en un pasillo.
 */

import { useState } from 'react';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { Async, Button, Empty, Modal, Note } from '@/components/ui';
import { Icon } from '@/components/common/NavIcons';

interface Item { id: string; name: string; consumption_unit: string }

/** Sin tildes ni mayúsculas: «papel» encuentra «Papél» y «PAPEL». */
const plano = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function EmployeeInsumos() {
  const query = useApi<{ items: Item[] }>('/employee/inventory-items');
  const [elegido, setElegido] = useState<Item | null>(null);
  const [busca, setBusca] = useState('');

  return (
    <>
      <section className="msec">
        <div className="msec__title">Registrar insumos</div>
        <p className="small muted" style={{ margin: '0 0 10px' }}>
          Tocá un producto para registrar que llegó o que se usó.
        </p>
        <Async query={query} empty={(d) => d.items.length === 0 && (
          <Empty icon="inventario" title="No hay productos cargados"
            hint="El administrador los da de alta en Inventario." />
        )}>
          {(data) => {
            const q = plano(busca.trim());
            const items = q ? data.items.filter((i) => plano(i.name).includes(q)) : data.items;
            return (
              <>
                {/* El buscador aparece con la lista, no antes: con tres
                    productos no hace falta, pero cuando son treinta, escribir
                    dos letras gana a scrollear con guantes. */}
                <label className="input buscador">
                  <Icon name="buscar" size={16} />
                  <input
                    className="buscador__campo"
                    type="search"
                    placeholder="Buscar producto"
                    aria-label="Buscar producto"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                  />
                </label>
                {items.length === 0 ? (
                  <Empty icon="buscar" title="Ningún producto con ese nombre"
                    hint="Probá con otra palabra." />
                ) : (
                  <div className="insumo-grid">
                    {items.map((item) => (
                      <button key={item.id} type="button" className="insumo-card"
                        onClick={() => setElegido(item)}>
                        <span className="insumo-card__ico" aria-hidden="true">
                          <Icon name="inventario" size={20} />
                        </span>
                        <strong>{item.name}</strong>
                        <span className="insumo-card__unidad">por {item.consumption_unit}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            );
          }}
        </Async>
      </section>

      {elegido && <MovimientoModal item={elegido} onClose={() => setElegido(null)} />}
    </>
  );
}

type Kind = 'ENTRADA' | 'CONSUMO';

function MovimientoModal({ item, onClose }: { item: Item; onClose: () => void }) {
  const [kind, setKind] = useState<Kind | null>(null);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');

  const mutation = useApiMutation(
    () => api.post('/employee/inventory-movements', {
      item_id: item.id,
      kind,
      quantity: qty,
      note: note.trim() || undefined,
    }),
    // El stock del admin se deriva de estos movimientos; la lista del
    // empleado no muestra stock, pero invalidarla no cuesta nada.
    ['/employee/inventory'],
  );

  // El stepper manda, pero el número también se puede escribir: 24 rollos no
  // deberían ser 23 toques. El techo espeja al del servidor.
  const fijar = (n: number) => setQty(Math.max(1, Math.min(999, Math.round(n))));

  /* El `Modal` del sistema, no una copia: trae el cierre, el portal a body y
     lo que se le agregue después (Escape, foco) sin repetirlo acá. */
  return (
    <Modal
      title={item.name}
      onClose={onClose}
      footer={mutation.isSuccess ? (
        <Button variant="primary" block onClick={onClose}>Cerrar</Button>
      ) : (
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!kind || mutation.isPending}
            onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Enviando…' : 'Registrar'}
          </Button>
        </>
      )}
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      {mutation.isSuccess && (
        <Note tone="ok">
          {kind === 'ENTRADA' ? 'Listo, quedó registrada la entrada.' : 'Listo, quedó registrado el consumo.'}
        </Note>
      )}

      {!mutation.isSuccess && (
        <>
          <div className="insumo-acciones">
            <button type="button" aria-pressed={kind === 'ENTRADA'}
              className={`insumo-accion${kind === 'ENTRADA' ? ' is-on' : ''}`}
              onClick={() => setKind('ENTRADA')}>
              <strong>Llegó</strong>
              <span>Entró producto</span>
            </button>
            <button type="button" aria-pressed={kind === 'CONSUMO'}
              className={`insumo-accion${kind === 'CONSUMO' ? ' is-on' : ''}`}
              onClick={() => setKind('CONSUMO')}>
              <strong>Se usó</strong>
              <span>Salió producto</span>
            </button>
          </div>

          <div className="stepper" role="group" aria-label="Cantidad">
            <button type="button" className="stepper__btn" aria-label="Restar uno"
              disabled={qty <= 1} onClick={() => fijar(qty - 1)}>−</button>
            <span className="stepper__mid">
              <input className="stepper__num" type="number" inputMode="numeric"
                min={1} max={999} value={qty} aria-label="Cantidad"
                onChange={(e) => fijar(Number(e.target.value) || 1)} />
              {/* La unidad tal cual la cargó el admin: pluralizarla a mano
                  fabricaría «unidads». */}
              <span className="stepper__unidad">{item.consumption_unit}</span>
            </span>
            <button type="button" className="stepper__btn" aria-label="Sumar uno"
              onClick={() => fijar(qty + 1)}>+</button>
          </div>

          <input className="input" placeholder="Nota (opcional)" maxLength={300}
            value={note} onChange={(e) => setNote(e.target.value)} />
        </>
      )}
    </Modal>
  );
}
